<script setup lang="ts">
import { computed, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import SeatsTable from '@/components/Admin/SeatsTable.vue'
import { studentModule } from '@/store/student/module'
import { organizationModule } from '@/store/organization/module'
import { licenseModule } from '@/store/license/module'
import { analyticsModule } from '@/store/analytics/module'
import { examModule } from '@/store/exam/module'
import { bundleModule } from '@/store/bundle/module'
import * as Sentry from '@sentry/vue'

const { SidePanel, Button: PocketButton, Textarea: PocketTextarea } = UIKit

const props = withDefaults(
    defineProps<{
        disabledExamIds?: string[]
        studentFirstName: string
        organizationName: string
        orgStudentId: string
        organizationId: string
    }>(),
    { disabledExamIds: () => [] }
)

const emit = defineEmits<{
    close: [value?: boolean]
    loading: [isLoading: boolean]
}>()

const selectedExamIds = ref<string[]>([])
const customMessage = ref('')

const studentLicenses = computed(() => {
    return licenseModule.getters.getLicensesForStudentId(props.orgStudentId)
})

const studentEmail = computed(() => {
    return studentLicenses.value?.[0]?.userEmail
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const checkedExamsChanged = (checkedRowIds: string[]) => {
    selectedExamIds.value = checkedRowIds
}

const emitLoading = (isLoading: boolean) => {
    emit('loading', isLoading)
}

const emitClose = () => {
    emit('close', true)
}

const submitNewSelectedExams = async () => {
    if (!currentOrganization.value) {
        return
    }

    emitLoading(true)
    await studentModule.actions.addStudentToExam({
        orgExamIds: selectedExamIds.value,
        orgStudentId: props.orgStudentId,
        organizationId: props.organizationId,
        customMessage: customMessage.value,
    })
    const examGuids = selectedExamIds.value.map(eid => {
        const exam = currentOrgExams.value[eid]
        return exam && examMetadata.value[exam.examMetadata.objectId]?.examGuid || ''
    })
    const bundles = selectedExamIds.value.map(id => bundleModule.getters.getExamBundle(id))
    analyticsModule.actions.amplitudeTrack(
        'Student Exams Added',
        {
            organizationId: currentOrganization.value.objectId,
            examGuids,
            examGuid: examGuids.length > 1 ? undefined : examGuids[0],
            bundleId: bundles[0]?.objectId,
        }
    )
    if (bundles.length > 1) {
        Sentry.captureException(
            'Student assigned exams across multiple bundles',
            {
                extra: {
                    orgStudentId: props.orgStudentId,
                    bundleIds: bundles.map(b => b?.objectId),
                    organizationId: currentOrganization.value.objectId,
                    examGuids,
                },
            }
        )
    }
    organizationModule.actions.validateOrgState()
    emitLoading(false)
    emitClose()
}
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            :name="`Add Exam Prep${props.studentFirstName ? ` for ${props.studentFirstName}` : ''}`"
            :isTeachSidePanel="true"
            class="select-exams"
            @close="emitClose"
        >
            <div class="select-exams__title">
                {{ props.studentFirstName || studentEmail }} will be emailed an invitation to join
                {{ props.organizationName }} for the selected exam(s).
            </div>

            <SeatsTable
                class="select-exams__seats"
                :show-checkboxes="true"
                theme="condensed"
                :hide-archived-seats="true"
                :disabled-exam-ids="props.disabledExamIds"
                @checkedRowsChanged="checkedExamsChanged"
            />

            <div class="select-exams__personalize">
                <PocketTextarea
                    v-model="customMessage"
                    label="Add a note to the invite email (optional)"
                    height="114px"
                    class="select-exams__text-field"
                />
            </div>

            <template #action>
                <PocketButton
                    type="primary"
                    :disabled="!selectedExamIds.length"
                    @click="submitNewSelectedExams"
                >
                    Add {{ selectedExamIds.length }} Exam{{ selectedExamIds.length !== 1 ? 's' : '' }}
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.select-exams {
    &__title {
        font-size: 14px;
        line-height: 19px;
        margin-bottom: 46px;
        max-width: 368px;
    }

    &__seats {
        margin-bottom: 52px;
        margin-left: -11px;
    }

    &__text-field {
        margin-left: -11px;
    }
}
</style>
